import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { isValidEmail, isPlausiblePhone, fullName } from '@/lib/validation';
import { INTEREST_TAGS } from '@/lib/interestTags';
import type {
  CustomerDetail, CustomerNote, CustomerRecord, CustomerTransaction, Favourite, KeyDate, ProductHit,
} from '@/api/customers';
import { cacheGet, cacheSet } from '@/lib/swrCache';
import { useComposer } from '@/components/comms/ComposerProvider';
import '@/styles/screens.css';

type Status = 'loading' | 'ready' | 'error';

const COMING_SOON = ['Mail history', 'Reminders'];

const eur = new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' });
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' });

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtKeyDate(k: KeyDate): string {
  return `${k.day} ${MONTHS[k.month - 1]}${k.year ? ` ${k.year}` : ''}`;
}

/** Customer detail (SPEC §7): editable contact (→ Shopify), read-only BC loyalty
 * + in-store history, and Shopify online orders. Consent chips wire to Klaviyo in M4. */
export function CustomerDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const composer = useComposer();

  const [status, setStatus] = useState<Status>('loading');
  const [detail, setDetail] = useState<CustomerDetail | null>(null);

  const load = () => {
    // Instant paint from the session cache (stale-while-revalidate).
    const cached = cacheGet<CustomerDetail>(`custdetail:${id}`);
    if (cached) { setDetail(cached); setStatus('ready'); }
    else setStatus('loading');
    api.get(id)
      .then((d) => { cacheSet(`custdetail:${id}`, d); setDetail(d); setStatus('ready'); })
      .catch(() => { if (!cached) setStatus('error'); });
  };
  useEffect(load, [id, api]);

  if (status === 'loading') {
    return (
      <div className="screen">
        <div className="skeleton-row" style={{ height: 80, marginBottom: 20 }} />
        <div className="skeleton-row" style={{ height: 180 }} />
      </div>
    );
  }
  if (status === 'error' || !detail) {
    return (
      <div className="screen">
        <p className="state-msg state-msg--error">Couldn&rsquo;t load this customer.</p>
        <div className="detail-actions">
          <button className="btn btn--ghost" onClick={() => navigate(-1)}>Back</button>
          <button className="btn" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  const { customer, activity } = detail;

  return (
    <div className="screen">
      <header className="detail-header">
        <div style={{ flex: 1 }}>
          <h1 className="detail-name">{fullName(customer.first_name, customer.last_name)}</h1>
          <p className="detail-store">{customer.store_name} ({customer.store_code})</p>
          <div className="chips">
            {activity && (activity.txns_12m > 0 || activity.spend_total > 0) && (
              <>
                <span className="chip">{activity.txns_12m} txns · {eur.format(activity.spend_12m)} (12 mo)</span>
                <span className="chip">{eur.format(activity.spend_total)} total</span>
                {activity.last_visit && <span className="chip">Last visit {fmtDate(activity.last_visit)}</span>}
              </>
            )}
            {customer.shopify_sync_status === 'retry' && (
              <span className="chip">Shopify sync pending</span>
            )}
          </div>
        </div>
        <div className="detail-actions">
          <button
            className="btn btn--sm"
            onClick={() => composer.openEmail([{
              customer_id: customer.customer_id,
              name: fullName(customer.first_name, customer.last_name),
              email: customer.email,
            }])}
          >
            <i className="ti ti-mail" aria-hidden="true" /> Email
          </button>
          <button className="btn btn--ghost" onClick={() => navigate(-1)}>Back</button>
        </div>
      </header>

      <InterestsSection
        interests={detail.interests}
        onToggle={(tag, on) => api.setInterest(id, tag, on)}
        onChanged={(interests) => setDetail((d) => (d ? { ...d, interests } : d))}
      />

      <ContactSection
        detail={detail}
        onSaved={(c) => setDetail((d) => (d ? { ...d, customer: c } : d))}
        save={(patch) => api.update(id, patch)}
      />

      <LinkedAccountsSection
        customer={customer}
        createShopify={() => api.createShopifyIdentity(id)}
        onChanged={(c) => setDetail((d) => (d ? { ...d, customer: c } : d))}
      />

      <FavouritesSection
        favourites={detail.favourites}
        onChanged={(favourites) => setDetail((d) => (d ? { ...d, favourites } : d))}
        search={(q, signal) => api.searchProducts(q, signal)}
        add={(fav) => api.addFavourite(id, fav)}
        remove={(favId) => api.removeFavourite(id, favId)}
      />

      <KeyDatesSection
        keyDates={detail.keyDates}
        onChanged={(keyDates) => setDetail((d) => (d ? { ...d, keyDates } : d))}
        add={(kd) => api.addKeyDate(id, kd)}
        remove={(kdId) => api.removeKeyDate(id, kdId)}
      />

      <TransactionsSection
        customerId={id}
        load={(limit, offset) => api.transactions(id, limit, offset)}
        addNote={(body, txn) => api.addNote(id, body, txn)}
      />

      <NotesSection
        notes={detail.notes}
        add={(body) => api.addNote(id, body)}
        onChanged={(notes) => setDetail((d) => (d ? { ...d, notes } : d))}
      />

      {COMING_SOON.map((label) => (
        <section className="section section--coming" key={label}>
          <p className="section-title">{label}</p>
          <p className="coming-label">Coming soon.</p>
        </section>
      ))}
    </div>
  );
}

// ── Favourites (tag products from the BC-synced catalogue) ──────────────────

function FavouritesSection({
  favourites, onChanged, search, add, remove,
}: {
  favourites: Favourite[];
  onChanged: (f: Favourite[]) => void;
  search: (q: string, signal?: AbortSignal) => Promise<ProductHit[]>;
  add: (fav: { sku: string; productName: string; note?: string }) => Promise<Favourite[]>;
  remove: (favId: string) => Promise<Favourite[]>;
}) {
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<ProductHit[]>([]);
  const [busy, setBusy] = useState(false);
  const debouncedQ = useDebouncedValue(q, 250);

  useEffect(() => {
    const term = debouncedQ.trim();
    if (!adding || term.length < 2) { setHits([]); return; }
    const ctrl = new AbortController();
    search(term, ctrl.signal).then(setHits).catch(() => {});
    return () => ctrl.abort();
  }, [debouncedQ, adding, search]);

  async function pick(p: ProductHit) {
    setBusy(true);
    try {
      onChanged(await add({ sku: p.item_no, productName: p.description }));
      setQ(''); setHits([]); setAdding(false);
    } finally { setBusy(false); }
  }

  return (
    <section className="section">
      <div className="section-head">
        <p className="section-title">Favourites</p>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setAdding((v) => !v)}>
          {adding ? 'Cancel' : 'Tag a product'}
        </button>
      </div>

      {adding && (
        <div className="picker">
          <input
            autoFocus className="picker-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search products by name or SKU"
          />
          {hits.length > 0 && (
            <div className="picker-results">
              {hits.map((p) => (
                <button key={p.item_no} type="button" className="picker-row" disabled={busy} onClick={() => pick(p)}>
                  <span>{p.description}</span>
                  <span className="picker-sku">{p.item_no}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {favourites.length === 0 ? (
        <p className="coming-label">No favourites tagged yet.</p>
      ) : (
        <div className="chips">
          {favourites.map((f) => (
            <span key={f.favourite_id} className="chip chip--fav">
              {f.product_name} <span className="chip-sku">{f.sku}</span>
              <button type="button" className="chip-x" aria-label={`Remove ${f.product_name}`}
                onClick={async () => onChanged(await remove(f.favourite_id))}>
                <i className="ti ti-x" aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

// ── Key dates ────────────────────────────────────────────────────────────────

function KeyDatesSection({
  keyDates, onChanged, add, remove,
}: {
  keyDates: KeyDate[];
  onChanged: (k: KeyDate[]) => void;
  add: (kd: { type: string; label?: string; month: number; day: number; year?: number; source?: string }) => Promise<KeyDate[]>;
  remove: (kdId: string) => Promise<KeyDate[]>;
}) {
  const [adding, setAdding] = useState(false);
  const [type, setType] = useState<'birthday' | 'anniversary' | 'custom'>('anniversary');
  const [label, setLabel] = useState('');
  const [month, setMonth] = useState(1);
  const [day, setDay] = useState(1);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      onChanged(await add({ type, label: label.trim() || undefined, month, day }));
      setLabel(''); setAdding(false);
    } finally { setBusy(false); }
  }

  return (
    <section className="section">
      <div className="section-head">
        <p className="section-title">Key dates</p>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setAdding((v) => !v)}>
          {adding ? 'Cancel' : 'Add key date'}
        </button>
      </div>

      {adding && (
        <div className="keydate-form">
          <select value={type} onChange={(e) => setType(e.target.value as typeof type)}>
            <option value="anniversary">Anniversary</option>
            <option value="birthday">Birthday</option>
            <option value="custom">Custom</option>
          </select>
          <input placeholder="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} />
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} aria-label="Month">
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={day} onChange={(e) => setDay(Number(e.target.value))} aria-label="Day">
            {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
          </select>
          <button type="button" className="btn btn--primary btn--sm" disabled={busy} onClick={save}>Add</button>
        </div>
      )}

      {keyDates.length === 0 ? (
        <p className="coming-label">No key dates yet.</p>
      ) : (
        <div className="result-list">
          {keyDates.map((k) => (
            <div key={k.key_date_id} className="result-row" style={{ cursor: 'default' }}>
              <span>
                <span className="result-row-name">{k.label || k.type} · {fmtKeyDate(k)}</span>
                {k.source && <span className="result-row-meta" style={{ display: 'block' }}>{k.source}</span>}
              </span>
              <button type="button" className="chip-x" aria-label="Remove key date"
                onClick={async () => onChanged(await remove(k.key_date_id))}>
                <i className="ti ti-x" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ── Interest tags (toggle) ──────────────────────────────────────────────────

function InterestsSection({
  interests,
  onToggle,
  onChanged,
}: {
  interests: string[];
  onToggle: (tag: string, on: boolean) => Promise<string[]>;
  onChanged: (interests: string[]) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const active = new Set(interests);

  async function toggle(tag: string) {
    if (busy) return;
    setBusy(tag);
    try {
      const updated = await onToggle(tag, !active.has(tag));
      onChanged(updated);
    } catch {
      /* leave state as-is on failure */
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="section">
      <p className="section-title">Interests</p>
      <div className="tag-toggles">
        {INTEREST_TAGS.map((t) => {
          const on = active.has(t.key);
          return (
            <button
              key={t.key}
              type="button"
              className={`tag-toggle${on ? ' is-on' : ''}`}
              aria-pressed={on}
              disabled={busy === t.key}
              onClick={() => toggle(t.key)}
            >
              <i className={`ti ${on ? 'ti-check' : 'ti-plus'}`} aria-hidden="true" />
              {t.label}
            </button>
          );
        })}
      </div>
    </section>
  );
}

// ── Editable contact (writes back via Shopify) ──────────────────────────────

function ContactSection({
  detail,
  onSaved,
  save,
}: {
  detail: CustomerDetail;
  onSaved: (c: CustomerRecord) => void;
  save: (patch: { firstName?: string; lastName?: string; phone?: string; email?: string }) => Promise<{ customer: CustomerRecord }>;
}) {
  const c = detail.customer;
  const [firstName, setFirstName] = useState(c.first_name ?? '');
  const [lastName, setLastName] = useState(c.last_name ?? '');
  const [phone, setPhone] = useState(c.phone ?? '');
  const [email, setEmail] = useState(c.email ?? '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const emailError = email.trim() && !isValidEmail(email) ? 'Enter a valid email.' : null;
  const phoneError = phone.trim() && !isPlausiblePhone(phone) ? 'Enter a valid phone number.' : null;
  const dirty =
    firstName !== (c.first_name ?? '') || lastName !== (c.last_name ?? '') ||
    phone !== (c.phone ?? '') || email !== (c.email ?? '');

  async function onSave() {
    if (emailError || phoneError) return;
    setSaving(true); setMsg(null); setErr(null);
    try {
      const updated = await save({ firstName, lastName, phone, email });
      onSaved(updated.customer);
      setMsg('Saved. Contact details updated in Shopify.');
    } catch {
      setErr('Update failed. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="section">
      <p className="section-title">Details</p>

      <div className="field-row">
        <div className="field">
          <label className="field-label" htmlFor="d-fn">First name</label>
          <input id="d-fn" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="d-ln">Last name</label>
          <input id="d-ln" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
      </div>

      <div className="field">
        <label className="field-label" htmlFor="d-ph">Phone</label>
        <input id="d-ph" value={phone} onChange={(e) => setPhone(e.target.value)} />
        {phoneError && <p className="field-error">{phoneError}</p>}
      </div>

      <div className="field">
        <label className="field-label" htmlFor="d-em">Email</label>
        <input id="d-em" value={email} onChange={(e) => setEmail(e.target.value)} />
        {emailError && <p className="field-error">{emailError}</p>}
      </div>

      {msg && <p className="field-label" style={{ color: 'var(--olive-deep)' }}>{msg}</p>}
      {err && <p className="field-error">{err}</p>}

      <div className="detail-actions">
        <button
          className="btn btn--primary"
          disabled={!dirty || saving || !!emailError || !!phoneError}
          onClick={onSave}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </section>
  );
}

// ── In-store purchases (local mirror; items + per-receipt notes, SPEC-2) ─────

const TXN_PAGE = 10;

function TransactionsSection({
  customerId, load, addNote,
}: {
  customerId: string;
  load: (limit: number, offset: number) => Promise<import('@/api/customers').TransactionPage>;
  addNote: (body: string, txn: { storeNo: string; posTerminalNo: string; transactionNo: number }) => Promise<CustomerNote[]>;
}) {
  const [txns, setTxns] = useState<CustomerTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [open, setOpen] = useState<string | null>(null); // expanded receipt key

  const key = (t: CustomerTransaction) => `${t.store_no}|${t.pos_terminal_no}|${t.transaction_no}`;

  const loadPage = useCallback((p: number) => {
    const cacheKey = `custtxns:${customerId}:${p}`;
    const cached = cacheGet<{ results: CustomerTransaction[]; total: number }>(cacheKey);
    if (cached) { setTxns(cached.results); setTotal(cached.total); setState('ready'); }
    else setState('loading');
    load(TXN_PAGE, p * TXN_PAGE)
      .then((res) => {
        cacheSet(cacheKey, { results: res.results, total: res.total });
        setTxns(res.results); setTotal(res.total); setState('ready');
      })
      .catch(() => { if (!cached) setState('error'); });
  }, [load, customerId]);

  useEffect(() => { loadPage(page); }, [loadPage, page]);

  const totalPages = Math.max(1, Math.ceil(total / TXN_PAGE));

  return (
    <section className="section">
      <div className="section-head">
        <p className="section-title">In-store purchases</p>
        {total > 0 && <span className="section-count">{total} receipt{total === 1 ? '' : 's'}</span>}
      </div>

      {state === 'loading' && <div className="skeleton-row" style={{ height: 60 }} />}
      {state === 'error' && (
        <p className="state-msg state-msg--error">
          Couldn&rsquo;t load purchases. <button className="btn btn--ghost btn--sm" onClick={() => loadPage(page)}>Retry</button>
        </p>
      )}
      {state === 'ready' && txns.length === 0 && (
        <p className="coming-label">No in-store purchases recorded (history from Jan 2024).</p>
      )}

      {state === 'ready' && txns.length > 0 && (
        <>
          <div className="result-list">
            {txns.map((t) => {
              const k = key(t);
              const expanded = open === k;
              return (
                <div key={k} className="txn-row">
                  <button type="button" className="result-row txn-row-head" onClick={() => setOpen(expanded ? null : k)}>
                    <span>
                      <span className="result-row-name">
                        {eur.format(t.amount)} · {t.store_name ?? t.store_no}
                        {t.channel === 'wholesale'
                          ? <span className="row-badge row-badge--wholesale">Wholesale</span>
                          : <span className="row-badge row-badge--app">App</span>}
                        {t.refunded && <span className="row-badge row-badge--refund">Refunded</span>}
                        {t.note && <span className="row-badge row-badge--note">Note</span>}
                      </span>
                      <span className="result-row-meta" style={{ display: 'block' }}>
                        {fmtDate(t.txn_date)}{t.quantity != null ? ` · ${t.quantity} item${t.quantity === 1 ? '' : 's'}` : ''}
                      </span>
                    </span>
                    <span className="txn-chevron">{expanded ? '▾' : '▸'}</span>
                  </button>
                  {expanded && (
                    <TxnDetail txn={t} addNote={addNote} onNoteAdded={(n) => {
                      setTxns((list) => list.map((x) => (key(x) === k ? { ...x, note: n } : x)));
                    }} />
                  )}
                </div>
              );
            })}
          </div>
          {totalPages > 1 && (
            <div className="pager">
              <button className="btn btn--ghost btn--sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>← Previous</button>
              <span className="pager-status">Page {page + 1} of {totalPages}</span>
              <button className="btn btn--ghost btn--sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next →</button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function TxnDetail({
  txn, addNote, onNoteAdded,
}: {
  txn: CustomerTransaction;
  addNote: (body: string, ref: { storeNo: string; posTerminalNo: string; transactionNo: number }) => Promise<CustomerNote[]>;
  onNoteAdded: (n: CustomerNote) => void;
}) {
  const [noteText, setNoteText] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  async function saveNote() {
    const body = noteText.trim();
    if (!body) return;
    setBusy(true);
    try {
      await addNote(body, { storeNo: txn.store_no, posTerminalNo: txn.pos_terminal_no, transactionNo: txn.transaction_no });
      onNoteAdded({ note_id: 'local', body, author_name: null, created_at: new Date().toISOString() });
      setNoteText(''); setEditing(false);
    } finally { setBusy(false); }
  }

  return (
    <div className="txn-detail">
      {txn.lines.length === 0 ? (
        <p className="coming-label">No line detail for this receipt.</p>
      ) : (
        <table className="txn-lines">
          <tbody>
            {txn.lines.map((l) => (
              <tr key={l.line_no}>
                <td className="txn-line-qty">{l.quantity != null ? `${l.quantity}×` : ''}</td>
                <td>{l.description ?? l.item_no ?? '—'}</td>
                <td className="txn-line-amt">{l.price_paid != null ? eur.format(l.price_paid) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {txn.note && (
        <p className="txn-note">
          <strong>Note:</strong> {txn.note.body}
          {txn.note.author_name && <span className="txn-note-author"> — {txn.note.author_name}</span>}
        </p>
      )}
      {!txn.note && !editing && (
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditing(true)}>Add note to this receipt</button>
      )}
      {editing && (
        <div className="note-editor">
          <textarea
            autoFocus rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)}
            placeholder="Note for this receipt (Guest only — never leaves this tool)"
          />
          <div className="detail-actions">
            <button className="btn btn--sm" disabled={busy || !noteText.trim()} onClick={saveNote}>Save note</button>
            <button className="btn btn--ghost btn--sm" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Customer notes (Guest-only, SPEC-2) ──────────────────────────────────────

function NotesSection({
  notes, add, onChanged,
}: {
  notes: CustomerNote[];
  add: (body: string) => Promise<CustomerNote[]>;
  onChanged: (notes: CustomerNote[]) => void;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      onChanged(await add(body));
      setText('');
    } finally { setBusy(false); }
  }

  return (
    <section className="section">
      <p className="section-title">Notes</p>
      <div className="note-editor">
        <textarea
          rows={2} value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Add a note about this customer (Guest only — never leaves this tool)"
        />
        <div className="detail-actions">
          <button className="btn btn--sm" disabled={busy || !text.trim()} onClick={save}>Add note</button>
        </div>
      </div>
      {notes.length === 0 ? (
        <p className="coming-label">No notes yet.</p>
      ) : (
        <div className="note-list">
          {notes.map((n) => (
            <div key={n.note_id} className="note-item">
              <p className="note-body">{n.body}</p>
              <p className="note-meta">
                {n.author_name ?? 'Unknown'} · {fmtDate(n.created_at)}
              </p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ── Linked accounts (SPEC-2 §4/§4a): show every identity link WITH its id ────

function LinkedAccountsSection({
  customer, createShopify, onChanged,
}: {
  customer: CustomerRecord;
  createShopify: () => Promise<{ customer: CustomerRecord }>;
  onChanged: (c: CustomerRecord) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shopifyId = customer.shopify_customer_gid
    ? customer.shopify_customer_gid.split('/').pop()
    : null;

  async function doCreateShopify() {
    setBusy(true); setError(null);
    try {
      const res = await createShopify();
      onChanged(res.customer);
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? 'Couldn’t create the Shopify customer.');
    } finally { setBusy(false); }
  }

  return (
    <section className="section">
      <p className="section-title">Linked accounts</p>
      <div className="linked-rows">
        <div className="linked-row">
          <span className={`row-badge row-badge--web${customer.shopify_customer_gid ? '' : ' row-badge--off'}`}>Shopify</span>
          {customer.shopify_customer_gid ? (
            <span className="linked-id">Customer #{shopifyId}</span>
          ) : (
            <>
              <span className="linked-none">Not linked</span>
              <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={doCreateShopify}>
                {busy ? 'Creating…' : 'Create Shopify customer'}
              </button>
            </>
          )}
        </div>
        <div className="linked-row">
          <span className={`row-badge row-badge--wholesale${customer.bc_customer_id ? '' : ' row-badge--off'}`}>BC member</span>
          {customer.bc_customer_id ? (
            <span className="linked-id">Contact {customer.bc_customer_id}</span>
          ) : (
            <>
              <span className="linked-none">Not linked</span>
              <button
                type="button" className="btn btn--ghost btn--sm" disabled
                title="Coming soon — creating wholesale accounts from Guest is planned once BC write-back is agreed"
              >
                Create wholesale account
              </button>
            </>
          )}
        </div>
      </div>
      {error && <p className="state-msg state-msg--error">{error}</p>}
      <p className="coming-label" style={{ marginTop: 10 }}>
        Contact edits push to Shopify when linked. BC write-back is planned, not yet live.
      </p>
    </section>
  );
}
